/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetState, useLatest, useLockFn, useUpdateEffect } from 'ahooks';
import lookie from 'lookie';
import { Button, Switch } from 'antd-mobile';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { Decimal } from 'decimal.js';
import { Icon } from '@fuxi/eevee-icon';
import { convertObjectKeysToCamelCase } from 'common-package';
import { klona } from 'klona';
import { get, omit } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import {
  CheckArm,
  Header,
  HeaderMemberInfo,
  Model2D,
  RobotExceptionPrompt,
  ScrollView,
  ServiceProcessCrumb,
  SlideSelect,
  Stepper,
  Toast,
  When,
} from '@/components';
import { useChassisStatePrompt, useNav } from '@/hooks';
import { genMinMaxSteps } from '@/components/stepper';
import { RobotExceptionPromptType } from '@/components/robot-exception-prompt';
import { handleNavFromOrderInfo } from '@/utils';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { CheckArmRefAttrs } from '@/components/check-arm';
import {
  ALL_PAGES,
  BODY_PART,
  MASSAGE_SETTING_CACHE,
  MassageType2Text,
  SELECTED_CUSTOMER_ID,
  ShockwaveType,
  UPPER_STATUS_CODE,
} from '@/constant';
import service from '@/service';
import { controItemExtranDom, CUSTOM_SOLUTION_BG, SOLUTION_TO_LABEL_COORDS } from './utils';
import UserGuideTips from './user-guide-tips';

import cx from './index.module.scss';

interface Props {}

const MassageSetting: React.FC<Props> = () => {
  const nav = useNav();
  const dispatch = useDispatch();
  const [isOpenManualAssist, setIsOpenManualAssist, getIsOpenManualAssist] = useGetState(true);
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [massageSettingsOptions, setMassageSettingsOptions] = useState<GetMassageSettingsOptionsResponse>(null);
  const [isConfirmingPlan, setIsConfirmingPlan] = useState(false);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [isGettingSettingOptions, setIsGettingSettingOptions] = useState(true);
  const checkArmRef = useRef<CheckArmRefAttrs>(null);
  const robotExceptionPromptRef = useRef<RobotExceptionPromptType>(null);
  const [productInfo, setProductInfo] = useState<GetProductResponse>(null);
  const [isRenderModelOK, setIsRenderModelOK] = useState(false);
  const [settingValues, setSettingValues, getSettingValues] = useGetState<
    Partial<Record<Massage.ControlItemOptions, number>>
  >({
    'robot.force': 60,
    'robot.velocity': 10,
    'robot.height': 100,
    'shockwave.power': 30,
    'shockwave.frequency': 10,
    'shockwave.basic_hits': 2500,
    'shockwave.nodule_hits': 300,
    'radio_frequency.power': 10,
    'radio_frequency.duration': 30,
    /** 热砭石 */
    'hot_stone.duration': 30,
    // /** 矩阵式艾灸默认数据 */
    'moxibustion.acupoint_area': 1,
    'moxibustion.tech': 1,
    'moxibustion.duration': 5,
    'hot_stone.power': 15,
    'hot_stone.temperature_level': 1,
    'hot_stone.mode': 1,
  });
  const isIgnoreTaskFinished = useRef(false);
  const latestOrder = useLatest(currentServingOrder);
  const shouldClearSettingCache = useRef(false);

  const hasSettingOptions = useMemo(() => currentServingOrder?.step === 'MassageSetting', [currentServingOrder?.step]);

  const openAssistantTips = useMemo(
    () =>
      currentServingOrder?.productItems?.includes('Shockwave')
        ? '开启人工补充方案后，最多可以打击2000次。可根据实际需求提前结束订单'
        : currentServingOrder?.productItems?.includes('RadioFrequency')
        ? '开启人工补充方案后，可根据实际需求提前结束订单'
        : '',
    [currentServingOrder?.productItems]
  );
  const latestOpenAssistantTips = useLatest(openAssistantTips);

  const isOnlyManual = useMemo(() => productInfo?.onlyManual, [productInfo?.onlyManual]);

  /** 是否显示开启人工治疗的开关 */
  const showManualAssistantSwitch = useMemo(
    () => !isOnlyManual && massageSettingsOptions?.[0]?.massageItem !== 'HotStone',
    [isOnlyManual, massageSettingsOptions]
  );

  const { loopGetDeviceChassisState } = useChassisStatePrompt();

  useUpdateEffect(() => {
    isOpenManualAssist && latestOpenAssistantTips.current && Toast.show(latestOpenAssistantTips.current);
  }, [isOpenManualAssist]);

  const modelSolution: Massage.ModelSolution = useMemo(() => {
    // return 'waist-rf-lc-1';
    if (currentServingOrder?.productItems?.includes('Shockwave')) {
      return currentServingOrder?.modelConfig?.find(item => item.key === 'Shockwave')?.value;
    } else {
      const modelValue = currentServingOrder?.modelConfig?.[0]?.value;
      if (modelValue === 'massage-shoulder-lv-dynamic') {
        return 'massage-shoulder-lv';
      }
      if (modelValue === 'massage-shoulder-lv-v2-dynamic') {
        return 'massage-shoulder-lv-v2';
      }
      if (modelValue === 'massage-yzc-zhai-dynamic') {
        return 'massage-yzc-zhai';
      }
      if (modelValue === 'massage-waist-lv-dynamic') {
        return 'massage-waist-lv';
      }
      return modelValue;
    }
  }, [currentServingOrder?.modelConfig, currentServingOrder?.productItems]);

  // 获取用户设置的按摩方案并还原
  const getMassageUserSettings = useCallback(
    async (servingOrder: GetServiceOrderListResponse) => {
      const { orderId, storeGoodsId } = servingOrder || {};
      if (!orderId) return;

      const backupSetting = klona(settingValues);

      const { data: productInfo } = await service.service.getProduct(storeGoodsId);

      setProductInfo(productInfo);

      try {
        // 通过接口数据设置默认值
        const { data: options } = await service.massage.getMassageSettingsOptions(orderId);

        const formatOptions = convertObjectKeysToCamelCase(options);

        setMassageSettingsOptions(formatOptions);

        formatOptions.forEach(item => {
          if (productInfo?.onlyManual) {
            // 人工治疗
            if (
              ['Shockwave', 'RadioFrequency', 'HotStone'].includes(item.massageItem) &&
              item.massageType === 'ManualMassage'
            ) {
              item.controlItemConfigs?.forEach(item2 => {
                const primaryValue = item2.default?.value as any;
                // 处理默认值为0的场景
                backupSetting[item2.name] = primaryValue || primaryValue === 0 ? +primaryValue : null;
              });
            }
          } else {
            // 机器治疗
            if (
              ['Shockwave', 'RadioFrequency', 'HotStone'].includes(item.massageItem) &&
              item.massageType === 'Massage'
            ) {
              item.controlItemConfigs?.forEach(item2 => {
                const primaryValue = item2.default?.value as any;
                backupSetting[item2.name] = primaryValue || primaryValue === 0 ? +primaryValue : null;
              });
            }
          }
        });
      } catch (e) {
        console.error('error', e);
      } finally {
        setIsGettingSettingOptions(false);
        scrollViewRef.current?.handleRefresh();
      }

      // 回显本地保存的数据
      try {
        const storageCache = lookie.get(MASSAGE_SETTING_CACHE) as {
          [orderId: string]: typeof settingValues & {
            isOpenManualAssist: boolean;
          };
        };
        const storageData = storageCache?.[latestOrder?.current?.orderId];

        if (storageData) {
          setSettingValues(omit(storageData, 'isOpenManualAssist'));
          setIsOpenManualAssist(get(storageData, 'isOpenManualAssist'));
        } else {
          // 本地无缓存，取接口默认值
          setSettingValues(backupSetting);
        }
      } catch (e) {
        console.log('回显数据失败', e);
      }
    },
    [latestOrder, setIsOpenManualAssist, setSettingValues, settingValues]
  );

  const initData = useCallback(async () => {
    const { data: servingOrder } = await service.service.getServingOrderByOperator();
    if (servingOrder?.orderId) {
      dispatch(setCurrentServingOrder(servingOrder));
      lookie.set(SELECTED_CUSTOMER_ID, servingOrder?.customerId);
    } else {
      Toast.show('当前任务已结束');
      nav(ALL_PAGES.MEMBER_DETAIL);
      return;
    }

    getMassageUserSettings(servingOrder);
  }, [dispatch, getMassageUserSettings, nav]);

  // 生成人工单默认参数
  const generateDefaultManualSettings = useCallback(() => {
    return massageSettingsOptions
      ?.find(item => item.massageType === 'ManualMassage')
      ?.controlItemConfigs?.reduce((accur, item2) => {
        switch (item2.name) {
          case 'shockwave.power':
            accur['power'] = +item2.default.value;
            break;
          case 'radio_frequency.power':
            accur['power'] = new Decimal(item2.default.value || 0).div(100).toNumber();
            break;
          case 'shockwave.basic_hits':
            accur['hits'] = +item2.default.value || 2000;
            break;
          case 'shockwave.frequency':
            accur['frequency'] = +item2.default.value;
            break;
          case 'radio_frequency.duration':
            accur['duration'] = +item2.default.value || 60;
            break;
          case 'hot_stone.duration':
            accur['duration'] = +item2.default.value;
            break;
        }
        return accur;
      }, {});
  }, [massageSettingsOptions]);

  // 确认并开始按摩
  const handleConfirmAndStart = useCallback(async () => {
    const currentProduct = currentServingOrder?.productItems?.[0];

    try {
      // 按摩/艾灸
      if (['Massage', 'Moxibustion'].includes(currentProduct)) {
        await service.massage.startRoboticTask({
          serviceOrderId: currentServingOrder?.orderId,
        });
      }
      // 磁震波
      else if (currentProduct === 'Shockwave') {
        const reqParams = [];

        if (isOnlyManual) {
          reqParams.push({
            power: settingValues['shockwave.power'],
            frequency: settingValues['shockwave.frequency'],
            hits: settingValues['shockwave.basic_hits'],
          });
        } else {
          reqParams.push({
            force: settingValues['robot.force'],
            power: settingValues['shockwave.power'],
            frequency: settingValues['shockwave.frequency'],
            basic_hits: settingValues['shockwave.basic_hits'],
            nodule_hits: settingValues['shockwave.nodule_hits'],
          });
        }

        !isOnlyManual && isOpenManualAssist && reqParams.push(generateDefaultManualSettings());

        await service.massage.startRoboticTask({
          serviceOrderId: currentServingOrder.orderId,
          shockwaveSettings: JSON.stringify(reqParams),
        });
      }
      // 内源热能
      else if (currentProduct === 'RadioFrequency') {
        const reqParams = [];

        if (isOnlyManual) {
          reqParams.push({
            power: new Decimal(settingValues['radio_frequency.power'] || 0).div(100).toNumber(),
            duration: settingValues['radio_frequency.duration'],
          });
        } else {
          reqParams.push({
            power: new Decimal(settingValues['radio_frequency.power'] || 0).div(100).toNumber(),
            duration: settingValues['radio_frequency.duration'],
            force: settingValues['robot.force'],
          });
        }

        !isOnlyManual && isOpenManualAssist && reqParams.push(generateDefaultManualSettings());

        await service.massage.startRoboticTask({
          serviceOrderId: currentServingOrder.orderId,
          radioFrequencySettings: JSON.stringify(reqParams),
        });
      }
      // 热砭石（仅机器治疗）
      else if (currentProduct === 'HotStone') {
        const reqParams: Array<{
          duration: number;
          power: number;
          temperatureLevel: number;
          mode?: number;
          force?: number;
        }> = [
          {
            duration: settingValues['hot_stone.duration'],
            power: new Decimal(settingValues['hot_stone.power'] || 0).div(100).toNumber(),
            temperatureLevel: settingValues['hot_stone.temperature_level'],
          },
        ];

        // 是否人工单
        if (isOnlyManual) {
          const mode = massageSettingsOptions
            ?.find(item => item.massageType === 'ManualMassage')
            ?.controlItemConfigs?.find(item => item.name === 'hot_stone.mode');
          /** 服务端有配置模式才传该参数 */
          mode && (reqParams[0].mode = settingValues['hot_stone.mode']);
        } else {
          reqParams[0].force = settingValues['robot.force'];

          const mode = massageSettingsOptions
            ?.find(item => item.massageType === 'Massage')
            ?.controlItemConfigs?.find(item => item.name === 'hot_stone.mode');
          /** 服务端有配置模式才传该参数 */
          mode && (reqParams[0].mode = settingValues['hot_stone.mode']);
        }

        await service.massage.startRoboticTask({
          serviceOrderId: currentServingOrder.orderId,
          hotStoneSettings: JSON.stringify(reqParams),
        });
      }

      const { data } = await service.service.getServingOrderByOperator();

      shouldClearSettingCache.current = true;
      // 判断当前订单状态是否正常
      const currentStep = await service.lanInterface.getCurrentStep(currentServingOrder.deviceId);
      if (!currentStep?.step) {
        robotExceptionPromptRef.current?.showOrderErrorPrompt({
          orderId: currentServingOrder?.orderId,
        });
        return;
      }
      await handleNavFromOrderInfo(data, nav);
      dispatch(setCurrentServingOrder(data));
    } catch (e) {
      console.error(e);
      setIsConfirmingPlan(false);
      if (e?.code === UPPER_STATUS_CODE.ChassisLockingOrUnLocking) {
        loopGetDeviceChassisState(currentServingOrder?.deviceId);
      } else {
        e?.message && Toast.show(e?.message);
      }
    }
  }, [
    currentServingOrder,
    dispatch,
    generateDefaultManualSettings,
    isOnlyManual,
    isOpenManualAssist,
    loopGetDeviceChassisState,
    massageSettingsOptions,
    nav,
    settingValues,
  ]);

  const handleCheckArm = useLockFn(
    useCallback(async () => {
      setIsConfirmingPlan(true);

      const { data: order } = await service.service.getServingOrderByOperator();
      if (!order) {
        Toast.show('检测到长时间未操作，已为您自动结束订单');
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }

      try {
        checkArmRef.current?.setMassageProcess('order-before');
        await checkArmRef.current?.handleCheckArm(currentServingOrder?.device);
      } catch (e) {
        e?.error !== 'Visual' && setIsConfirmingPlan(false);
      }
    }, [currentServingOrder?.device, nav])
  );

  useEffect(() => {
    initData();
    const orderId = latestOrder.current;
    return () => {
      if (shouldClearSettingCache.current) {
        lookie.remove(MASSAGE_SETTING_CACHE);
      } else {
        lookie.set(MASSAGE_SETTING_CACHE, {
          [orderId?.orderId]: {
            isOpenManualAssist: getIsOpenManualAssist(),
            ...getSettingValues(),
          },
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('massage-setting')}>
      <Header
        title={
          <HeaderMemberInfo
            name={`${currentServingOrder?.customerName}`}
            handleClick={() => {}}
            avatarUrl={currentServingOrder?.avatarUrl ?? currentServingOrder?.userAvatar}
          />
        }
        backConfig={{ text: '会员详情', action: () => nav(ALL_PAGES.MEMBER_DETAIL) }}
      />
      <UserGuideTips />
      <CheckArm onCheckArmPass={handleConfirmAndStart} ref={checkArmRef} />
      <ServiceProcessCrumb className={cx('crumb')} />
      <RobotExceptionPrompt
        socketInstance={null}
        ref={robotExceptionPromptRef}
        isIgnoreTaskFinished={isIgnoreTaskFinished}
      />
      {!isGettingSettingOptions ? (
        <div className={cx('view-wrapper', { 'show-right': hasSettingOptions })}>
          <div className={cx('left')}>
            <div className={cx('title')}>
              <div className={cx('name')}>{currentServingOrder?.productName}</div>
              <div className={cx('massage-type')}>
                <span className={cx('massage-type-text', currentServingOrder?.productItems?.[0])}>
                  {MassageType2Text[currentServingOrder?.productItems?.[0]]}
                </span>
              </div>
            </div>
            <div className={cx('model-wrapper')}>
              {/* 功效文本展示 */}
              {isRenderModelOK && !!SOLUTION_TO_LABEL_COORDS[modelSolution] && productInfo?.techFeature && (
                <div
                  className={cx('intro-label')}
                  style={{
                    top: SOLUTION_TO_LABEL_COORDS[modelSolution]?.top,
                    right: SOLUTION_TO_LABEL_COORDS[modelSolution]?.right,
                  }}
                >
                  <div
                    className={cx('label-arrow')}
                    style={
                      SOLUTION_TO_LABEL_COORDS[modelSolution]?.arrowCoords
                        ? {
                            top: SOLUTION_TO_LABEL_COORDS[modelSolution]?.arrowCoords?.top,
                          }
                        : {}
                    }
                  />
                  <div className={cx('intro-title')}>手法特色</div>
                  <div className={cx('intro-content')}>{productInfo?.techFeature}</div>
                </div>
              )}
              {/* 自定义模型贴图，非konva绘制 */}
              {isRenderModelOK && CUSTOM_SOLUTION_BG[modelSolution]?.imageUrl && (
                <div className={cx('custom-solution-bg')} style={omit(CUSTOM_SOLUTION_BG[modelSolution], 'imageUrl')}>
                  <img src={CUSTOM_SOLUTION_BG[modelSolution]?.imageUrl} alt="" />
                </div>
              )}
              <Model2D
                modelSolution={modelSolution}
                onlyRenderSolutionMainBg={!!CUSTOM_SOLUTION_BG[modelSolution]?.imageUrl}
                bodyNodeList={[]}
                onRenderModelOK={() => setIsRenderModelOK(true)}
              />
            </div>
            <div className={cx('plan-info')}>
              <div className={cx('plan-info-left')}>
                {!currentServingOrder?.onlyManual && (
                  <>
                    <div className={cx('info-item')}>
                      <div className={cx('label')}>功效介绍：</div>
                      <div className={cx('value')}>{productInfo?.effectIntro}</div>
                    </div>
                    <div className={cx('info-item', 'item-middle')}>
                      <div className={cx('label')}>适合人群：</div>
                      <div className={cx('value')}>{productInfo?.targetGroup}</div>
                    </div>
                  </>
                )}
                <div className={cx('plan-info-left-footer')}>
                  <div className={cx('footer-info-item')}>
                    <div className={cx('label')}>创建时间：</div>
                    <div className={cx('value')}>{currentServingOrder?.createTime}</div>
                  </div>
                  <div className={cx('footer-info-item')}>
                    <div className={cx('label')}>机器人：</div>
                    <div className={cx('value')}>{currentServingOrder?.deviceId}</div>
                  </div>
                  {/* 纯人工理疗不展示部位 */}
                  {currentServingOrder?.bodyPart !== 'All' && (
                    <div className={cx('footer-info-item')}>
                      <div className={cx('label')}>部位：</div>
                      <div className={cx('value')}>{BODY_PART[currentServingOrder?.bodyPart]}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className={cx('plan-info-right', { show: !hasSettingOptions })}>
                <Button className={cx('btn')} color="primary" loading={isConfirmingPlan} onClick={handleCheckArm}>
                  确认并开始
                </Button>
              </div>
            </div>
          </div>
          <div className={cx('right')}>
            {showManualAssistantSwitch && (
              <div className={cx('manual-assist')}>
                <div className={cx('label')}>人工辅助治疗</div>
                <div className={cx('tips')}>
                  <Icon name="tips" className={cx('tips-icon')} />
                  <span>开启后，机器理疗结束后将进行人工辅助理疗</span>
                </div>
                <Switch
                  onChange={setIsOpenManualAssist}
                  checked={isOpenManualAssist}
                  style={{
                    '--width': '48px',
                    '--height': '28px',
                    '--border-width': '2px',
                  }}
                />
              </div>
            )}
            <ScrollView ref={scrollViewRef} className={cx('setting-box-wrapper')}>
              <div className={cx('setting-box')}>
                {massageSettingsOptions
                  ?.find(item => item.massageType === (isOnlyManual ? 'ManualMassage' : 'Massage'))
                  ?.controlItemConfigs.filter(
                    item => !!controItemExtranDom[item.name]?.label && item.name !== 'shockwave.nodule_hits'
                  )
                  // 人工治疗不显示打线设置
                  // ?.filter(item => (isOnlyManual ? item.name !== 'shockwave.basic_hits' : !!item))
                  ?.map(item => (
                    <div key={item.name} className={cx('setting-item')}>
                      <div className={cx('label')}>
                        {isOnlyManual && item.name === 'shockwave.basic_hits'
                          ? '打击次数'
                          : controItemExtranDom[item.name]?.label}
                        <When condition={item.name === 'hot_stone.power' && settingValues['hot_stone.power'] >= 60}>
                          <Icon name="electric" className={cx('electric-icon')} />
                        </When>
                      </div>
                      <div className={cx('setter')}>
                        {item.name === 'hot_stone.mode' ? (
                          <SlideSelect
                            activeColor="white"
                            key={item.name}
                            activeBold={true}
                            steps={
                              massageSettingsOptions
                                ?.find(item => item.massageType === (isOnlyManual ? 'ManualMassage' : 'Massage'))
                                ?.controlItemConfigs.find(item => item.name === 'hot_stone.mode')?.values
                            }
                            value={settingValues[item.name]}
                            onChange={value =>
                              setSettingValues(preValue => {
                                const clonedValue = klona(preValue);
                                clonedValue[item.name] = value as any;
                                return clonedValue;
                              })
                            }
                          />
                        ) : (
                          <Stepper
                            value={settingValues[item.name]}
                            {...genMinMaxSteps(item.values)}
                            onChange={value => {
                              if (
                                item.name === 'shockwave.frequency' &&
                                value >= 12 &&
                                currentServingOrder?.shockwaveType === ShockwaveType['DEJI']
                              ) {
                                Toast.show('超过12Hz理疗渗透力会降低哦');
                              }
                              setSettingValues(preValue => {
                                const clonedValue = klona(preValue);
                                clonedValue[item.name] = value;
                                return clonedValue;
                              });
                            }}
                            suffix={controItemExtranDom[item.name]?.suffix}
                          />
                        )}
                      </div>
                      <When condition={item.name === 'hot_stone.power' && settingValues['hot_stone.power'] >= 60}>
                        <div className={cx('hot-stone-power-tips')}>
                          <Icon name="tips" className={cx('icon')} />
                          <span>强度60%及以上酥麻感较强，请沟通后使用</span>
                        </div>
                      </When>
                    </div>
                  ))}
              </div>
            </ScrollView>
            <div className={cx('btns')}>
              {currentServingOrder?.stepIsBackable && (
                <Button
                  className={cx('sub-btn')}
                  onClick={async () => {
                    const { data: order } = await service.service.getServingOrderByOperator();
                    if (!order) {
                      Toast.show('检测到长时间未操作，已为您自动结束订单');
                      nav(ALL_PAGES.MEMBER_DETAIL);
                      return;
                    }
                    nav(
                      order?.productItems?.includes('Shockwave') ? ALL_PAGES.ACTION_TEST : ALL_PAGES.BODY_MEASUREMENT
                    );
                  }}
                >
                  上一步
                </Button>
              )}
              <Button color="primary" loading={isConfirmingPlan} className={cx('confirm-btn')} onClick={handleCheckArm}>
                确认并开始
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className={cx('loading-wrapper')}>
          <Loading3QuartersOutlined className={cx('loading-icon')} spin style={{ fontSize: '34px' }} />
          <span>网络加载中，请稍后...</span>
        </div>
      )}
    </div>
  );
};

export default MassageSetting;
